import { Jobibot } from '@/components/JobiBot.tsx'
import { LanguageSelector } from '@/components/LanguageSelector.tsx'
import { resetAuthenticatedTracking, trackAuthenticatedOnce } from '@/trackAuthenticatedOnce'
import { logger } from '@jobilla/instrumentation'
import { useCandidateAuthLogout } from '@jobilla/nexus-sdk/lib/hooks/useCandidateAuth'
import { useCandidateOrganisationIndex } from '@jobilla/nexus-sdk/lib/hooks/useCandidateOrganisation'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, Navigate, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Centered } from '../components/Centered'

export const Route = createFileRoute('/organisations/')({
	head: () => ({
		meta: [{ title: 'Jobilla' }],
	}),
	component: OrganisationsScreen,
})

function OrganisationsScreen() {
	const { t } = useTranslation('common')
	const navigate = useNavigate()
	const queryClient = useQueryClient()

	const organisationsQuery = useCandidateOrganisationIndex({ retry: 0 })
	const logout = useCandidateAuthLogout({
		onSuccess: () => {
			logger.track('Candidate Center: Logged out')
			resetAuthenticatedTracking()
			queryClient.clear()
			void navigate({ to: '/', replace: true })
		},
	})

	useEffect(() => {
		if (!organisationsQuery.isSuccess) {
			return
		}
		trackAuthenticatedOnce()
		logger.track('Candidate Center: Viewed organisations', {
			data: { organisation_count: organisationsQuery.data.organisations.length },
		})
		// Fire once per successful load — not again when the count changes on refetch.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [organisationsQuery.isSuccess])

	if (organisationsQuery.isPending) {
		return (
			<Centered>
				<p className="text-sm text-grey-500">{t('login.loading')}</p>
			</Centered>
		)
	}

	if (organisationsQuery.isError) {
		if (organisationsQuery.error.response?.status === 401) {
			return <Navigate to="/" replace={true} />
		}

		return (
			<Centered>
				<p className="text-lg font-medium text-grey-800">{t('login.error.title')}</p>
				<p className="max-w-sm text-sm text-grey-500">{t('organisations.error')}</p>
			</Centered>
		)
	}

	const { organisations } = organisationsQuery.data

	return (
		<div className="mx-auto flex min-h-screen w-full max-w-md flex-col px-6 py-12">
			<header className="flex items-center justify-between gap-4">
				<div className="text-sm font-medium text-grey-800">
					<Jobibot size={42} />
				</div>
				<div className="flex items-center gap-3">
					<LanguageSelector />
					<button
						type="button"
						onClick={() => logout.mutate()}
						disabled={logout.isPending}
						className="cursor-pointer text-sm font-medium text-brand-600 hover:text-brand-700 disabled:cursor-not-allowed disabled:text-grey-400"
					>
						{logout.isPending ? t('organisations.logging_out') : t('organisations.log_out')}
					</button>
				</div>
			</header>

			<h1 className="mt-10 text-lg font-semibold text-grey-900">{t('organisations.title')}</h1>
			<p className="mt-1 text-sm text-grey-500">{t('organisations.intro')}</p>

			{organisations.length === 0
				? (
					<div className="mt-3 rounded-lg bg-grey-50 px-6 py-4">
						<p className="text-sm font-medium text-grey-800">{t('organisations.empty.title')}</p>
						<p className="mt-1 text-sm text-grey-500">{t('organisations.empty.body')}</p>
					</div>
				)
				: (
					<ul className="mt-3 flex flex-col gap-2">
						{organisations.map(organisation => (
							<li key={organisation.id}>
								<Link
									to="/organisations/$organisationId"
									params={{ organisationId: organisation.id }}
									onClick={() =>
										logger.track('Candidate Center: Opened organisation', {
											data: { candidacy_count: organisation.candidacyCount },
										})}
									className="flex items-center justify-between gap-4 rounded-lg bg-grey-50 px-6 py-4 hover:bg-grey-100"
								>
									<span className="min-w-0">
										<span className="block truncate text-lg font-medium text-grey-900">
											{organisation.name}
										</span>
										<span className="mt-0.5 block text-sm text-grey-700">
											{t('organisations.count', { count: organisation.candidacyCount })}
										</span>
									</span>
									<span aria-hidden={true} className="text-xl text-grey-400">›</span>
								</Link>
							</li>
						))}
					</ul>
				)}
		</div>
	)
}
